// Shared client-side PDF receipt helper.
//
// Uses jspdf (already on the dependency list) to render a single-page
// receipt that mirrors the pricing breakdown shown on the booking
// confirmation page. The helper is deliberately i18n-agnostic — callers
// pass already-translated labels and pre-formatted amounts so the same
// helper can be re-used across renter, trip-detail, and host-booking
// surfaces without duplicating any layout or library setup.
//
// Loaded with a dynamic import so jspdf (~250 kB gzipped) is only pulled
// into the bundle when a renter actually clicks "Download receipt".

export interface ReceiptLine {
  /** Already-translated label, e.g. "Rental fee". */
  label: string;
  /** Already-formatted amount with currency symbol, e.g. "$180.00". */
  amount: string;
}

export interface BookingReceiptInput {
  /** Document title shown at the top, e.g. "Booking receipt". */
  title: string;
  /** Brand name shown in the header, e.g. "BookAclassic". */
  brand: string;
  /** Short booking reference (e.g. first 8 chars of public_id, uppercased). */
  bookingReference: string;
  bookingReferenceLabel: string;
  /** Pre-formatted payment date, e.g. "1 May 2026". */
  paymentDate: string;
  paymentDateLabel: string;
  /** Pre-formatted summary lines (car, host, dates). */
  summary: Array<{ label: string; value: string }>;
  /** Pricing breakdown rows. Zero-amount rows should be filtered by the caller. */
  lines: ReceiptLine[];
  /** Total paid row, rendered as a bolder bottom line. */
  total: { label: string; amount: string };
  /** File name used by the download (e.g. "booking-receipt-ABC12345.pdf"). */
  filename: string;
  /** Optional footer line, e.g. "Thank you for booking with BookAclassic.". */
  footer?: string;
}

/**
 * Task #5968 — resolve the REAL "paid to BookAclassic" figure for a
 * partial-payment receipt.
 *
 * On a partial-payment booking the renter's card is charged only the
 * platform amount, and (for cross-border markets) in USD — the local rental
 * fee is settled directly with the host at pickup. A receipt that reports the
 * full local booking total therefore overstates what BookAclassic actually
 * collected and shows the wrong currency. This helper returns the true Stripe
 * settlement (amount + currency) read off the succeeded op=1 billing row
 * (`platformChargeAmount` / `platformChargeCurrency` on the trip payload) so
 * both the Trip Detail and Booking Confirmation receipts agree.
 *
 * Returns `null` — so the caller falls back to its existing full-total
 * behaviour (byte-identical for full-payment bookings) — when the booking is
 * not partial, or when the real charge is unavailable (legacy/imported or
 * as-yet unreconciled rows have no succeeded charge row, so the server sends
 * a null amount/currency). We never infer the USD figure by converting the
 * local amount.
 */
export function resolvePartialReceiptCharge(input: {
  paymentType: "full" | "partial" | null | undefined;
  platformChargeAmount: number | null | undefined;
  platformChargeCurrency: string | null | undefined;
}): { amount: number; currency: string } | null {
  if (input.paymentType !== "partial") return null;
  const amount = Number(input.platformChargeAmount);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const currency = (input.platformChargeCurrency ?? "").trim();
  if (!currency) return null;
  return { amount, currency };
}

/**
 * Render `input` as a PDF and trigger a browser download.
 *
 * The layout is intentionally simple (single page, A4) so it stays
 * predictable across browsers. Numeric columns are right-aligned so amounts
 * line up cleanly even when currencies differ across rows.
 */
export async function generateBookingReceiptPdf(input: BookingReceiptInput): Promise<void> {
  const { jsPDF } = await import("jspdf");

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 48;
  const right = pageWidth - margin;
  let y = margin;

  // ── Header: brand + title ──────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(120);
  doc.text(input.brand.toUpperCase(), margin, y);
  y += 22;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(20);
  doc.text(input.title, margin, y);
  y += 30;

  // ── Reference + payment date row ───────────────────────────────────
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(input.bookingReferenceLabel, margin, y);
  doc.text(input.paymentDateLabel, right - 200, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(20);
  doc.text(input.bookingReference, margin, y + 16);
  doc.text(input.paymentDate, right - 200, y + 16);
  y += 40;

  // ── Divider ────────────────────────────────────────────────────────
  doc.setDrawColor(220);
  doc.setLineWidth(0.5);
  doc.line(margin, y, right, y);
  y += 22;

  // ── Summary block ──────────────────────────────────────────────────
  doc.setFontSize(10);
  for (const row of input.summary) {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120);
    doc.text(row.label, margin, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20);
    const lines = doc.splitTextToSize(row.value, pageWidth - margin * 2 - 110);
    doc.text(lines, margin + 110, y);
    y += Math.max(18, lines.length * 14 + 4);
  }
  y += 6;

  // ── Divider ────────────────────────────────────────────────────────
  doc.setDrawColor(220);
  doc.line(margin, y, right, y);
  y += 22;

  // ── Pricing breakdown ──────────────────────────────────────────────
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  for (const line of input.lines) {
    doc.setTextColor(60);
    doc.text(line.label, margin, y);
    doc.text(line.amount, right, y, { align: "right" });
    y += 18;
  }

  y += 4;
  doc.setDrawColor(180);
  doc.line(margin, y, right, y);
  y += 20;

  // ── Total ──────────────────────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(20);
  doc.text(input.total.label, margin, y);
  doc.text(input.total.amount, right, y, { align: "right" });
  y += 30;

  // ── Footer ─────────────────────────────────────────────────────────
  if (input.footer) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(140);
    const lines = doc.splitTextToSize(input.footer, pageWidth - margin * 2);
    doc.text(lines, margin, y);
  }

  doc.save(input.filename);
}
